import React, { useState, useMemo } from 'react';
import useAxios from 'axios-hooks';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  CircularProgress,
  Toolbar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PrintIcon from '@material-ui/icons/Print';
import { IStripeOrder, IStripeOrderStatus } from '../types/strapi';
import { axiosClient } from '../helpers/AxiosClient';

const useStyles = makeStyles((theme) => ({
  paper: {
    margin: theme.spacing(3),
    padding: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  statusChip: {
    fontWeight: 'bold',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

const statusColors: Record<IStripeOrderStatus, 'default' | 'primary' | 'secondary'> = {
  [IStripeOrderStatus.paid]: 'default',
  [IStripeOrderStatus.prepared]: 'primary',
  [IStripeOrderStatus.shipped]: 'secondary',
};

const statusLabels: Record<IStripeOrderStatus, string> = {
  [IStripeOrderStatus.paid]: 'Pagato',
  [IStripeOrderStatus.prepared]: 'Preparato',
  [IStripeOrderStatus.shipped]: 'Spedito',
};

const DirectSaleOrders: React.FC = () => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(IStripeOrderStatus.paid);

  const [{ data: orders, loading, error }, refetch] = useAxios<IStripeOrder[]>({
    url: '/stripe-orders',
    params: {
      populate: ['user'],
    },
  });

  const filteredOrders = useMemo(() => {
    if (!orders) return [];

    return orders.filter((order) => {
      const matchesSearch =
        !searchQuery ||
        order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        `${order.shippingAddress.name} ${order.shippingAddress.surname}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, searchQuery, statusFilter]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = async (orderId: number, newStatus: IStripeOrderStatus) => {
    try {
      await axiosClient.put(`/stripe-orders/${orderId}`, { status: newStatus });
      refetch().catch(console.error);
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const handlePrint = async (orderId: number) => {
    try {
      const response = await axiosClient.get(`/stripe-orders/${orderId}/print`);
      const byteCharacters = atob(response.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error printing order:', error);
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Paper className={classes.paper}>
        <Typography color="error">Errore nel caricamento degli ordini</Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography variant="h4" gutterBottom>
        Ordini Online
      </Typography>

      <Toolbar className={classes.toolbar}>
        <TextField
          label="Cerca per numero ordine o cliente"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ minWidth: 300 }}
        />

        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Stato</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as string)} label="Stato">
            <MenuItem value="all">Tutti</MenuItem>
            <MenuItem value={IStripeOrderStatus.paid}>Pagato</MenuItem>
            <MenuItem value={IStripeOrderStatus.prepared}>Preparato</MenuItem>
            <MenuItem value={IStripeOrderStatus.shipped}>Spedito</MenuItem>
          </Select>
        </FormControl>
      </Toolbar>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Numero Ordine</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Città</TableCell>
              <TableCell align="right">Totale</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell align="center">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order) => (
              <TableRow key={order.id}>
                <TableCell>{order.orderNumber}</TableCell>
                <TableCell>{new Date(order.date).toLocaleDateString('it-IT')}</TableCell>
                <TableCell>
                  {order.shippingAddress.name} {order.shippingAddress.surname}
                </TableCell>
                <TableCell>{order.metadata?.customerEmail || '-'}</TableCell>
                <TableCell>{order.shippingAddress.city}</TableCell>
                <TableCell align="right">€ {order.total.toFixed(2)}</TableCell>
                <TableCell>
                  <FormControl size="small" variant="outlined">
                    <Select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order.id, e.target.value as IStripeOrderStatus)}
                    >
                      <MenuItem value={IStripeOrderStatus.paid}>
                        <Chip
                          label={statusLabels[IStripeOrderStatus.paid]}
                          color={statusColors[IStripeOrderStatus.paid]}
                          size="small"
                          className={classes.statusChip}
                        />
                      </MenuItem>
                      <MenuItem value={IStripeOrderStatus.prepared}>
                        <Chip
                          label={statusLabels[IStripeOrderStatus.prepared]}
                          color={statusColors[IStripeOrderStatus.prepared]}
                          size="small"
                          className={classes.statusChip}
                        />
                      </MenuItem>
                      <MenuItem value={IStripeOrderStatus.shipped}>
                        <Chip
                          label={statusLabels[IStripeOrderStatus.shipped]}
                          color={statusColors[IStripeOrderStatus.shipped]}
                          size="small"
                          className={classes.statusChip}
                        />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handlePrint(order.id)} size="small">
                    <PrintIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Righe per pagina:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
      />
    </Paper>
  );
};

export default DirectSaleOrders;
